// src/utils/date.js
// *****************************************************************************
// IMPORTS
// *****************************************************************************
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/pt';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import app from 'configs/ConfigIndex';
import translateX from 'utils/translateX';

// *****************************************************************************
// CONSTANTS
// *****************************************************************************
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

// *****************************************************************************
// EXPORT
// *****************************************************************************
export const formatDateTimeDefault = ({ date, showHours = true, showSeconds = false } = {}) => {
  // Force locale and derive pattern parts from the effective locale
  if (!date) return null;

  const appLocale = toCanonicalLocale(app.LOCALE || 'en-us');
  const loc = mapToDayjsLocale(appLocale);
  const prev = dayjs.locale();
  dayjs.locale(loc);

  const datePart = safeDatePatternL(loc);
  const timePart = buildTimePart(loc, showSeconds);

  const out = showHours
    ? dayjs(date).format(`${datePart} ${timePart}`)
    : dayjs(date).format(datePart);

  dayjs.locale(prev);
  return out;
};

export const getAgeDisplay = (date, isAge = false) => {
  // returns { visible: boolean, label: string }
  const info = computeAgeInfo(date);
  if (!info) return { visible: false, label: '' };

  if (info.unit === 'years') {
    const unit =
      info.value === 1
        ? translateX(isAge ? 'year_old' : 'year')
        : translateX(isAge ? 'years_old' : 'years');

    return { visible: true, label: `${info.value} ${unit.toLowerCase()}` };
  }

  if (info.unit === 'months') {
    const unit = info.value === 1 ? translateX('month') : translateX('months');
    return { visible: true, label: `${info.value} ${unit}` };
  }

  if (info.unit === 'days') {
    if (info.value === 0) {
      return { visible: true, label: translateX('today') || 'hoje' };
    }
    const unit = info.value === 1 ? translateX('day') : translateX('days');
    return { visible: true, label: `${info.value} ${unit}` };
  }

  return { visible: false, label: '' };
};

// *****************************************************************************
// HELPERS
// *****************************************************************************
function mapToDayjsLocale(appLocale) {
  const lc = String(appLocale || 'en-us').toLowerCase();
  if (['en', 'en-us', 'en-gb', 'en-ca', 'en-au', 'en-in', 'en-nz'].includes(lc)) return 'en';
  if (lc === 'pt-br') return 'pt-br';
  if (lc === 'pt' || lc === 'pt-pt') return 'pt';
  if (['es', 'es-es', 'es-mx', 'es-ar', 'es-cl'].includes(lc)) return 'es';
  if (['fr', 'fr-fr'].includes(lc)) return 'fr';
  if (['de', 'de-de'].includes(lc)) return 'de';
  if (['it', 'it-it'].includes(lc)) return 'it';
  if (['ja', 'ja-jp'].includes(lc)) return 'ja';
  if (lc === 'zh-cn') return 'zh-cn';
  if (lc === 'zh-tw') return 'zh-tw';
  return 'en';
}

function is12hLocale(loc) {
  // Keep 12h only for English default contexts
  return ['en'].includes(loc);
}

function buildTimePart(loc, showSeconds) {
  // Builds time format string based on locale and seconds preference
  if (is12hLocale(loc)) {
    return showSeconds ? 'hh:mm:ss A' : 'hh:mm A';
  }
  return showSeconds ? 'HH:mm:ss' : 'HH:mm';
}

function safeDatePatternL(loc) {
  // Use localeData 'L' if valid; override for locales that return ISO 'YYYY-MM-DD'
  const L = dayjs.localeData().longDateFormat('L');
  if (L && !/^Y{2,4}-M{1,2}-D{1,2}$/.test(L)) return L;

  // Fallbacks by locale
  switch (loc) {
    case 'pt-br':
    case 'pt':
      return 'DD/MM/YYYY';
    case 'en':
      return 'MM/DD/YYYY';
    case 'es':
    case 'fr':
    case 'de':
    case 'it':
      return 'DD/MM/YYYY';
    case 'zh-cn':
    case 'zh-tw':
    case 'ja':
      return 'YYYY/MM/DD';
    default:
      return 'YYYY-MM-DD';
  }
}

function computeAgeInfo(date) {
  // accepts dayjs, Date, string; returns { unit: 'days'|'months'|'years', value } or null
  if (!date) return null;
  const d = dayjs(date);
  if (!d.isValid()) return null;

  const today = dayjs().startOf('day');
  const birth = d.startOf('day');
  if (birth.isAfter(today)) return null;

  const years = today.diff(birth, 'year');
  if (years >= 1) return { unit: 'years', value: years };

  const months = today.diff(birth, 'month');
  if (months >= 2) return { unit: 'months', value: months };

  const days = today.diff(birth, 'day');
  return { unit: 'days', value: days };
}

function toCanonicalLocale(val = '') {
  // Normalize to lowercase for case-insensitive comparison
  const v = String(val).toLowerCase();
  if (v === 'pt-pt') return 'pt-pt'; // must be BEFORE startsWith('pt')
  if (v === 'pt' || v === 'br' || v === 'pt-br' || v.startsWith('pt')) return 'pt-br';
  if (v === 'en' || v === 'us' || v === 'en-us' || v.startsWith('en')) return 'en-us';
  if (v === 'es-es' || v === 'es' || v.startsWith('es')) return 'es-es';
  return 'en-us';
}
